import { PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { CommonItem } from "@sscs/types";
import {
  calculatePaginationOffset,
  convertSortToQueryString,
} from "@sscs/utils";
import { PROJECTS_ENDPOINT, SOFTWARE_CATALOG_ENDPOINT } from "Constants";
import { baseApi, projectsApi } from "Services";
import type { RootState } from "Store/store";
import { ImportExternalPackageResponse } from "Types";
import {
  BrowseCatalogRequest,
  BrowseCatalogResponse,
  BrowseExternalCatalogRequest,
  BrowseExternalCatalogResponse,
  ExternalFilterOptionsRequest,
  FilterOptionsRequest,
  ImportExternalPackageRequest,
  LikePackageRequest,
  SoftwareCatalogPlatformsRequest,
  SoftwareCatalogs,
} from "../types";
import { getPopularityAfterVote } from "../utils";

export const softwareCatalogApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getSoftwareCatalogPlatforms: build.query<
      SoftwareCatalogs,
      SoftwareCatalogPlatformsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/platforms`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
      providesTags: ["SoftwareCatalog"],
    }),
    getSoftwareCatalogItems: build.query<
      BrowseCatalogResponse,
      BrowseCatalogRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/browse`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
          name: queryArg.filter.name,
          publisher: queryArg.filter.publisher,
          category: queryArg.filter.category,
          license: queryArg.filter.license,
          in_catalog: queryArg.filter.in_catalog,
          sort_by: convertSortToQueryString(queryArg.sort),
          limit: queryArg.pagination.itemsPerPage,
          offset: calculatePaginationOffset(queryArg.pagination),
        },
      }),
      providesTags: (result) =>
        result?.items.map(({ package_id }) => ({
          type: "Package",
          id: package_id,
        })) ?? [],
    }),
    likePackage: build.mutation<void, LikePackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}/like`,
        method: "PUT",
        body: {
          like: queryArg.like,
        },
      }),
      // Optimistically update all cached results instead of refetching
      async onQueryStarted(
        { packageId, like },
        { dispatch, getState, queryFulfilled },
      ) {
        const state = getState();
        const cachedBrowseCatalogRequests =
          softwareCatalogApi.util.selectCachedArgsForQuery(
            state,
            "getSoftwareCatalogItems",
          );
        const cachedGetPackageRequests =
          projectsApi.util.selectCachedArgsForQuery(state, "getPackage");
        const patchResults: ReturnType<
          ReturnType<typeof softwareCatalogApi.util.updateQueryData>
        >[] = [];

        cachedGetPackageRequests.forEach((requestArgs) => {
          const patch = dispatch(
            projectsApi.util.updateQueryData(
              "getPackage",
              requestArgs,
              (draft) => {
                if (requestArgs.packageId === packageId) {
                  draft.user_like = like;
                }
              },
            ),
          );
          patchResults.push(patch);
        });

        cachedBrowseCatalogRequests.forEach((requestArgs) => {
          const patch = dispatch(
            softwareCatalogApi.util.updateQueryData(
              "getSoftwareCatalogItems",
              requestArgs,
              (draft) => {
                const selectedPackage = draft.items.find(
                  (item) => item.package_id === packageId,
                );

                if (selectedPackage) {
                  selectedPackage.popularity = getPopularityAfterVote(
                    selectedPackage.popularity,
                    selectedPackage.user_like,
                    like,
                  );
                  selectedPackage.user_like = like;
                }
              },
            ),
          );
          patchResults.push(patch);
        });

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      },
    }),
    getSoftwareCatalogCategories: build.query<
      CommonItem[],
      FilterOptionsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/categories`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
        },
      }),
    }),
    getSoftwareCatalogLicenses: build.query<CommonItem[], FilterOptionsRequest>(
      {
        query: (queryArg) => ({
          url: `${SOFTWARE_CATALOG_ENDPOINT}/licenses`,
          params: {
            group_id: queryArg.groupId,
            platform: queryArg.platform,
            architecture: queryArg.architecture,
            extension: queryArg.extension,
          },
        }),
      },
    ),
    getSoftwareCatalogPublishers: build.query<
      CommonItem[],
      FilterOptionsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/publishers`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
        },
      }),
    }),
    getExternalCatalogItems: build.query<
      BrowseExternalCatalogResponse,
      BrowseExternalCatalogRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${queryArg.catalog}/browse`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
          name: queryArg.filter.name,
          publisher: queryArg.filter.publisher,
          license: queryArg.filter.license,
          category: queryArg.filter.category,
          in_catalog: queryArg.filter.in_catalog,
          sort_by: convertSortToQueryString(queryArg.sort),
          limit: queryArg.pagination.itemsPerPage,
          offset: calculatePaginationOffset(queryArg.pagination),
        },
      }),
      providesTags: ["SoftwareCatalog"],
    }),
    getExternalCatalogCategories: build.query<
      CommonItem[],
      ExternalFilterOptionsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${queryArg.catalog}/categories`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
        },
      }),
    }),
    getExternalCatalogLicenses: build.query<
      CommonItem[],
      ExternalFilterOptionsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${queryArg.catalog}/licenses`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
        },
      }),
    }),
    getExternalCatalogPublishers: build.query<
      CommonItem[],
      ExternalFilterOptionsRequest
    >({
      query: (queryArg) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${queryArg.catalog}/publishers`,
        params: {
          group_id: queryArg.groupId,
          platform: queryArg.platform,
          architecture: queryArg.architecture,
          extension: queryArg.extension,
        },
      }),
    }),
    importExternalPackage: build.mutation<
      ImportExternalPackageResponse,
      ImportExternalPackageRequest
    >({
      query: ({ catalog, ...body }) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${catalog}/import`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SoftwareCatalog"],
    }),
  }),
});

export const versionDownloaded =
  ({
    packageId,
    versionId,
  }: {
    packageId: string;
    versionId: string;
  }): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(
      projectsApi.util.updateQueryData("getPackage", { packageId }, (draft) => {
        draft.download_count += 1;
      }),
    );

    softwareCatalogApi.util
      .selectCachedArgsForQuery(getState(), "getSoftwareCatalogItems")
      .forEach((requestArgs) => {
        dispatch(
          softwareCatalogApi.util.updateQueryData(
            "getSoftwareCatalogItems",
            requestArgs,
            (draft) => {
              const downloaded = draft.items.find(
                (item) => item.version.version_id === versionId,
              );

              if (downloaded) {
                downloaded.download_count += 1;
              }
            },
          ),
        );
      });
  };

export const {
  useGetSoftwareCatalogPlatformsQuery,
  useGetSoftwareCatalogItemsQuery,
  useGetExternalCatalogItemsQuery,
  useLikePackageMutation,
  useGetSoftwareCatalogCategoriesQuery,
  useGetSoftwareCatalogLicensesQuery,
  useGetSoftwareCatalogPublishersQuery,
  useGetExternalCatalogCategoriesQuery,
  useGetExternalCatalogLicensesQuery,
  useGetExternalCatalogPublishersQuery,
  useImportExternalPackageMutation,
} = softwareCatalogApi;
